import React, { useState } from 'react';
import { Search, Send, AlertCircle, CheckCircle, Loader2, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import { API_BASE_URL } from '../../config/api';;

const ShareCredits = () => {
  const { currentUser, getIdToken } = useAuth();
  const { credits, fetchUserCredits } = useCredits();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditsToShare, setCreditsToShare] = useState(1);
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState(null);
  const [transferSuccess, setTransferSuccess] = useState(null);
  
  // Search for users
  const handleSearch = async () => {
    if (searchTerm.length < 3) {
      setSearchError('Please enter at least 3 characters');
      return;
    }
    
    try {
      setSearching(true);
      setSearchError(null);
      setSearchResults([]);
      
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(`${API_BASE_URL}/user/search?email=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Search failed');
      }
      
      const data = await response.json();
      setSearchResults(data);
      
      if (data.length === 0) {
        setSearchError('No users found matching that email');
      }
    } catch (error) {
      console.error('User search error:', error);
      setSearchError(error.message || 'Failed to search for users');
    } finally {
      setSearching(false);
    }
  };
  
  // Handle key press in search field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  // Transfer credits
  const handleTransferCredits = async () => {
    if (!selectedUser) return;
    
    if (creditsToShare <= 0) {
      setTransferError('Please enter a valid number of credits');
      return;
    }
    
    if (creditsToShare > credits) {
      setTransferError('You don\'t have enough credits');
      return;
    }
    
    try {
      setTransferring(true);
      setTransferError(null);
      setTransferSuccess(null);
      
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(`${API_BASE_URL}/user/transfer-credits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: selectedUser.id,
          credits: creditsToShare
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Transfer failed');
      }
      
      setTransferSuccess(`Successfully shared ${creditsToShare} credits with ${selectedUser.email}`);
      
      // Reset form
      setCreditsToShare(1);
      setSelectedUser(null);
      setSearchTerm('');
      setSearchResults([]);
      
      // Refresh credits balance
      await fetchUserCredits();
    } catch (error) {
      console.error('Credit transfer error:', error);
      setTransferError(error.message || 'Failed to transfer credits');
    } finally {
      setTransferring(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Share Credits with Others</h2>
      
      {transferError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{transferError}</div>
        </div>
      )}
      
      {transferSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-start">
          <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{transferSuccess}</div>
        </div>
      )}
      
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-4">
          Search for other users by email to share your credits with them.
          You currently have <span className="font-bold text-blue-600">{credits} credits</span> available to share.
        </p>
        
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by email..."
              className="w-full p-2 pl-10 border rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
          </div>
          
          <button
            onClick={handleSearch}
            disabled={searching || searchTerm.length < 3}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center disabled:bg-gray-400"
          >
            {searching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </button>
        </div>
        
        {searchError && (
          <p className="text-sm text-red-500 mt-2">{searchError}</p>
        )}
      </div>
      
      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium text-gray-700 mb-2">Search Results</h3>
          <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
            {searchResults.map(user => (
              <div
                key={user.id}
                onClick={() => setSelectedUser(user)}
                className={`p-3 cursor-pointer hover:bg-gray-50 ${
                  selectedUser?.id === user.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <div className="font-medium">{user.displayName || user.email}</div>
                    <div className="text-sm text-gray-600">{user.email}</div>
                    {user.country && (
                      <div className="text-xs text-gray-500">
                        Country: {user.country}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Transfer form */}
      {selectedUser && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h3 className="font-medium text-gray-700 mb-3">
            Share Credits with {selectedUser.displayName || selectedUser.email}
          </h3>
          
          <div className="flex items-end space-x-4 mb-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Credits to Share
              </label>
              <input
                type="number"
                min="1"
                max={credits}
                value={creditsToShare}
                onChange={(e) => setCreditsToShare(Math.max(1, Math.min(credits, parseInt(e.target.value) || 0)))}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <button
              onClick={handleTransferCredits}
              disabled={transferring || creditsToShare <= 0 || creditsToShare > credits}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center disabled:bg-gray-400"
            >
              {transferring ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Share Credits
                </>
              )}
            </button>
          </div>
          
          <div className="text-sm text-gray-600">
            You will share {creditsToShare} credit{creditsToShare !== 1 ? 's' : ''} with {selectedUser.email}.
            This action cannot be undone.
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareCredits;